import { isSameDay } from "date-fns";
import moment from "moment-timezone";

const MAX_TITLE_LENGTH = 80;

// The post-tasks list returns `type_data` as a JSON string (sometimes encoded twice), not an object.
export function parseTypeData(raw) {
  let value = raw;
  for (let i = 0; i < 2 && typeof value === "string"; i++) {
    try {
      value = JSON.parse(value);
    } catch (error) {
      return {};
    }
  }
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function toFileIds(files) {
  if (Array.isArray(files)) {
    return files.filter((id) => id !== null && id !== undefined && id !== "");
  }
  return files ? [files] : [];
}

function firstLine(text) {
  const line = text.split("\n").find((l) => l.trim()) || "";
  const trimmed = line.trim();
  return trimmed.length > MAX_TITLE_LENGTH
    ? `${trimmed.slice(0, MAX_TITLE_LENGTH)}…`
    : trimmed;
}

// Everything the schedule UI shows about a task, with fallbacks for posts that have no title or no media.
export function getPostInfo(task) {
  const data = parseTypeData(task?.type_data);
  const text = typeof data.text === "string" ? data.text.trim() : "";
  const ownTitle = typeof data.title === "string" ? data.title.trim() : "";
  const title = ownTitle || firstLine(text);
  const kind =
    data.post_type === "reel" || data.post_type === "story"
      ? data.post_type
      : "post";

  // A Threads chain (OP#3469): part 1 plus its replies. Every task of the post carries the same type_data, so
  // the count is the same whichever account's task is shown.
  const replies = Array.isArray(data.threads?.replies) ? data.threads.replies : [];

  return {
    title,
    text: title === text ? "" : text,
    files: toFileIds(data.files),
    kind,
    type: task?.type,
    threadParts: 1 + replies.length,
  };
}

// One status for a post published to several accounts: any failure wins, then anything still pending.
export function getPostStatus(tasks) {
  const statuses = tasks.map((task) => task?.status);
  if (statuses.some((s) => s === "fail" || s === "failed")) return "failed";
  if (statuses.length > 0 && statuses.every((s) => s === "success")) {
    return "published";
  }
  return "scheduled";
}

// What the post composer needs to edit a scheduled post. A scheduled post is a parent post plus one task per account
// (and per occurrence when it repeats); `parentPost` is optional and only supplies the repeat settings.
export function buildScheduledEdit(tasks, parentPost) {
  const sorted = [...tasks].sort(
    (a, b) => new Date(a.schedule_utc_date) - new Date(b.schedule_utc_date)
  );
  const first = sorted[0];
  const accountIds = [...new Set(tasks.map((task) => task.account_id))];
  const when = moment
    .utc(first.schedule_utc_date)
    .tz(first.timezone || "UTC")
    .format("YYYY-MM-DDTHH:mm:ss");

  return {
    draftData: {
      id: first.parent_post_id,
      type_data: parseTypeData(first.type_data),
      tags: first.tags,
      accounts: accountIds.map((id) => ({ id })),
      schedule_date: when,
      repeat_freq: parentPost?.repeat_freq || "no_repeat",
      repeat_end: parentPost?.repeat_end || null,
      status: "scheduled",
    },
    replaceTaskIds: tasks.map((task) => task.id),
  };
}

export const ALL_GROUPS = "all";

// The tasks of one account group. "all", and a group that is gone (deleted since it was picked), leave the list as it is.
export function filterTasksByGroup(tasks, groups, groupId) {
  if (groupId === ALL_GROUPS) return tasks;
  const group = (groups || []).find((item) => String(item.id) === String(groupId));
  if (!group) return tasks;
  const accountIds = new Set((group.accounts || []).map(String));
  return tasks.filter((task) => accountIds.has(String(task.account_id)));
}

// The posts of one calendar day, earliest first. A post published to several accounts has one task per account;
// they collapse into the first task with `count` = how many.
export function getDayEntries(tasks, day) {
  const posts = new Map();
  tasks.forEach((task) => {
    if (!isSameDay(new Date(task.schedule_utc_date), day)) return;
    const known = posts.get(task.parent_post_id);
    if (known) {
      known.count += 1;
    } else {
      posts.set(task.parent_post_id, { ...task, count: 1 });
    }
  });
  return [...posts.values()].sort(
    (a, b) => new Date(a.schedule_utc_date) - new Date(b.schedule_utc_date)
  );
}
